/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `BuyerCompanyForm` for the Caintra Connect frontend.
 * @module pages/BuyerCompanyForm
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../utils/logger.js";
import styles from "../styles/BuyerCompanyForm.module.css";
import LanguageSelector from "../components/LanguageSelector/index.jsx";
import NotificationBell from "../components/NotificationBell/index.jsx";
import FileUploadArea from "../modals/ProposalCreateModal/FileUploadArea.jsx";
import { useNavigation } from "../context/NavigationContext";

import { CircularProgress, SvgIcon, Backdrop } from "@mui/material";

import { ReactComponent as TitleIcon } from "../assets/icons/BuyerCompanies/TitleIcon.svg";
import { ReactComponent as SaveIcon } from "../assets/icons/CompanyEditPage/SaveIcon.svg";
import { ReactComponent as ChevronLeftIcon } from "../assets/icons/CompanyEditPage/ChevronLeftIcon.svg";
import { ReactComponent as BuildingIcon } from "../assets/icons/BuyerCompanyCreation/BuildingIcon.svg";
import { ReactComponent as LocationIcon } from "../assets/icons/BuyerCompanyCreation/LocationIcon.svg";
import { ReactComponent as PersonIcon } from "../assets/icons/BuyerCompanyCreation/PersonIcon.svg";
import { ReactComponent as EmailIcon } from "../assets/icons/BuyerCompanyCreation/EmailIcon.svg";
import { ReactComponent as CheckCircleIcon } from "../assets/icons/BusinessEvents/CheckCircleIcon.svg";
import { ReactComponent as UploadFileIcon } from "../assets/icons/BuyerCompanyCreation/UploadFileIcon.svg";
import { ReactComponent as DownloadFileIcon } from "../assets/icons/BuyerCompanyCreation/DownloadFileIcon.svg";

import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { canAccessBuyerCompanies } from "../utils/accessControl";
import useRequireAccess from "../hooks/useRequireAccess";
import Select from "react-select";
import {
  selectStyles,
  getSafeSelectedOption,
  getSelectedOption,
  createFileObjectFromUrl,
  parseExcelFile,
  handleDownloadBuyerCompaniesExcelLayout,
  areReferenceIdsEqual,
  validateEmail,
  validateRFC,
} from "../utils/common.js";
import CustomerCompanyUsers from "../components/CustomerCompanyUsers/index.jsx";
import { 
  getCompanySizeOptions,
  getCountryCodeOptions } from "../data/selectOptions.js";
import {
  createBuyerCompany,
  updateBuyerCompany,
  processExcelFile,
} from "../logic/buyerCompany/index.js";
import ExcelErrorModal from "../modals/ExcelErrorModal/index.jsx";
import useBuyerCompanyReferenceData from "../hooks/useBuyerCompanyReferenceData";

const BYTES_PER_KILOBYTE = 1024;
const KILOBYTES_PER_MEGABYTE = 1024;
const MAX_LOGO_SIZE_MEGABYTES = 5;
const MAX_LOGO_SIZE_BYTES =
  MAX_LOGO_SIZE_MEGABYTES * KILOBYTES_PER_MEGABYTE * BYTES_PER_KILOBYTE;
const LOGO_MIME_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const SLIDE_UP_DURATION_MS = 300;
const EXCEL_SUCCESS_REDIRECT_MS = 3000;
const BUYER_COMPANIES_PATH = "/buyerCompanies";
const REFERENCE_LOADING_MESSAGE = "Loading...";

const logoRejectionKey = (file) => {
  if (!LOGO_MIME_TYPES.includes(file.type)) {
    return "pages.buyerCompanyForm.logoFileTypeError";
  }
  if (file.size > MAX_LOGO_SIZE_BYTES) {
    return "pages.buyerCompanyForm.logoFileSizeError";
  }
  return null;
};

const BuyerCompanyForm = () => {
  const { t } = useTranslation();
  const countryCodeOptions = getCountryCodeOptions(t);
  const navigate = useNavigate();
  const { allowed } = useRequireAccess(canAccessBuyerCompanies);
  const location = useLocation();
  const { updateNavigation } = useNavigation();
  const isEditMode = location.state?.isEditMode || false;
  const companyData = location.state?.companyData || null;
  const [logoFile, setLogoFile] = useState(
    createFileObjectFromUrl(companyData?.logo) || null,
  );
  const [isNewLogoUploaded, setIsNewLogoUploaded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState("");
  const [createdCompanyData, setCreatedCompanyData] = useState(null);
  const {
    states,
    towns,
    filteredTowns,
    setFilteredTowns,
    existingCompanies,
    loading,
    setLoading,
  } = useBuyerCompanyReferenceData(navigate);

  const addressStateRef = useRef(null);
  const addressTownRef = useRef(null);

  const companySizeOptions = getCompanySizeOptions(t);
  const [tamanoDeLaEmpresa, setTamanoDeLaEmpresa] = useState(
    companyData?.tamanoDeLaEmpresa || null,
  );

  const [razonSocial, setRazonSocial] = useState(
    companyData?.razonSocial || "",
  );
  const [nombreComercial, setNombreComercial] = useState(
    companyData?.nombreComercial || "",
  );
  const [RFC, setRFC] = useState(companyData?.RFC || "");
  const [direccion, setDireccion] = useState(companyData?.direccion || "");
  const [estado, setEstado] = useState(companyData?.estado || null);
  const [municipio, setMunicipio] = useState(companyData?.municipio || null);
  const [nombreContactoPrincipal, setNombreContactoPrincipal] = useState(
    companyData?.contactoPrincipal?.nombre || "",
  );
  const [
    correoElectronicoContactoPrincipal,
    setCorreoElectronicoContactoPrincipal,
  ] = useState(companyData?.contactoPrincipal?.correoElectronico || "");
  const [cargoContactoPrincipal, setCargoContactoPrincipal] = useState(
    companyData?.contactoPrincipal?.cargo || "",
  );

  const currentIsEditMode = isEditMode || createdCompanyData !== null;
  const currentCompanyData = createdCompanyData?.data || companyData;

  useEffect(() => {
    if (currentIsEditMode && currentCompanyData) {
      logger.info("currentCompanyData", { data: currentCompanyData });
      setRazonSocial(currentCompanyData.razonSocial || "");
      setNombreComercial(currentCompanyData.nombreComercial || "");
      setRFC(currentCompanyData.RFC || "");
      setTamanoDeLaEmpresa(
        currentCompanyData.tamanoDeLaEmpresa !== null &&
          currentCompanyData.tamanoDeLaEmpresa !== undefined
          ? currentCompanyData.tamanoDeLaEmpresa
          : null,
      );
      setDireccion(currentCompanyData.direccion || "");
      setEstado(currentCompanyData.estado || null);
      setMunicipio(currentCompanyData.municipio || null);
      setNombreContactoPrincipal(
        currentCompanyData.contactoPrincipal?.nombre || "",
      );
      setCorreoElectronicoContactoPrincipal(
        currentCompanyData.contactoPrincipal?.correoElectronico || "",
      );
      setCargoContactoPrincipal(
        currentCompanyData.contactoPrincipal?.cargo || "",
      );
      setLoading(false);
    }
  }, [currentIsEditMode, currentCompanyData, setLoading]);

  useEffect(() => {
    updateNavigation("/buyerCompanyForm", "buyerCompanies");
  }, [updateNavigation]);

  const [showError, setShowError] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [excelError, setExcelError] = useState("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isSlidingUp, setIsSlidingUp] = useState(false);
  const [invalidRows, setInvalidRows] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [showExcelErrorModal, setShowExcelErrorModal] = useState(false);

  // Debug excelError state changes
  useEffect(() => {
    logger.info("excelError state changed:", { data: excelError });
  }, [excelError]);

  const rejectLogoFile = useCallback(
    (messageKey, fileInput) => {
      setError(t(messageKey));
      setLogoFile(null);
      setIsNewLogoUploaded(false);
      if (fileInput) {
        fileInput.value = "";
      }
    },
    [t],
  );

  const acceptLogoFile = useCallback((file, fileInput) => {
    setLogoFile(file);
    setIsNewLogoUploaded(true);
    setError("");
    setHasChanges(true);
    setShowError(false);
    if (fileInput) {
      fileInput.value = "";
    }
  }, []);

  const storeLogoFile = useCallback(
    (file, fileInput) => {
      if (!file) {
        return;
      }
      const rejectionKey = logoRejectionKey(file);
      if (rejectionKey) {
        rejectLogoFile(rejectionKey, fileInput);
        return;
      }
      acceptLogoFile(file, fileInput);
    },
    [acceptLogoFile, rejectLogoFile],
  );

  const handleFileChange = useCallback(
    (event) => {
      const file = event.target.files[0];
      logger.info("file", { data: file });
      storeLogoFile(file, event.target);
    },
    [storeLogoFile],
  );

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      setIsDragging(false);
      storeLogoFile(event.dataTransfer.files[0]);
    },
    [storeLogoFile],
  );

  const handleFileClick = useCallback(() => {
    document.getElementById("logoFileInput").click();
  }, []);

  const handleInputChange = useCallback((setter, value) => {
    setter(value);
    setHasChanges(true);
    setShowError(false);
  }, []);

  const filterReferenceOption = useCallback((option, inputValue) => {
    if (!inputValue) {
      return true;
    }
    return option.data.nombre.toLowerCase().includes(inputValue.toLowerCase());
  }, []);

  const noReferenceOptionsMessage = useCallback(
    () => t("pages.register.noOptionsFound"),
    [t],
  );

  const referenceLoadingMessage = useCallback(
    () => REFERENCE_LOADING_MESSAGE,
    [],
  );

  const getReferenceOptionValue = useCallback((option) => option._id, []);

  const formatReferenceOptionLabel = useCallback(
    (option) => <span>{option.nombre}</span>,
    [],
  );

  const handleCompanySizeChange = useCallback(
    (selectedOption) => {
      logger.info("selectedOption", { data: selectedOption });
      handleInputChange(setTamanoDeLaEmpresa, selectedOption.value);
    },
    [handleInputChange],
  );

  const handleStateChange = useCallback(
    (selectedOption) => {
      if (!selectedOption) {
        return;
      }

      logger.info("selectedOption", { data: selectedOption });
      handleInputChange(setEstado, selectedOption._id);
      addressTownRef.current.clearValue();
      setMunicipio("");

      const filteredTownsForState = towns.filter((town) =>
        areReferenceIdsEqual(town.idEstado, selectedOption._id),
      );
      logger.info("Filtered towns for state:", {
        data: filteredTownsForState,
      });
      setFilteredTowns(filteredTownsForState);
    },
    [handleInputChange, setFilteredTowns, towns],
  );

  const handleTownChange = useCallback(
    (selectedOption) => {
      if (!selectedOption) {
        return;
      }

      logger.info("selectedOption", { data: selectedOption });
      handleInputChange(setMunicipio, selectedOption._id);
      handleInputChange(setEstado, String(selectedOption.idEstado));
    },
    [handleInputChange],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaveDisabled()) {
      setShowError(true);
      return;
    }
    setSaving(true);
    try {
      const buyerCompanyData = {
        razonSocial: razonSocial,
        nombreComercial: nombreComercial,
        RFC: RFC,
        tamanoDeLaEmpresa: tamanoDeLaEmpresa,
        direccion: direccion,
        estado: estado,
        municipio: municipio,
        contactoPrincipal: {
          nombre: nombreContactoPrincipal,
          correoElectronico: correoElectronicoContactoPrincipal,
          cargo: cargoContactoPrincipal,
        },
        ...(logoFile &&
          isNewLogoUploaded && {
            logo: {
              fieldName: "logo",
              fileName: logoFile.name,
            },
          }),
      };

      logger.info("buyerCompanyData", { data: buyerCompanyData });

      let data;
      if (currentIsEditMode) {
        data = await updateBuyerCompany(
          navigate,
          currentCompanyData._id,
          buyerCompanyData,
        );
      } else {
        data = await createBuyerCompany(navigate, buyerCompanyData);
      }

      logger.info("Response is ok and data is:", { data: data });
      logger.info("logoFile type:", { data: typeof logoFile });
      logger.info("logoFile instanceof File:", {
        data: logoFile instanceof File,
      });
      logger.info("logoFile details:", {
        data: {
          name: logoFile?.name,
          size: logoFile?.size,
          type: logoFile?.type,
        },
      });

      if (
        data.presignedUrl &&
        logoFile &&
        isNewLogoUploaded &&
        logoFile instanceof File
      ) {
        try {
          const uploadResponse = await fetch(data.presignedUrl, {
            method: "PUT",
            body: logoFile,
            headers: {
              "Content-Type": logoFile.type || "application/octet-stream",
            },
          });

          logger.info("uploadResponse:", { data: uploadResponse });

          if (!uploadResponse.ok) {
            throw new Error(
              `Failed to upload file: ${uploadResponse.statusText}`,
            );
          }

          logger.info("File uploaded successfully");
        } catch (uploadError) {
          logger.error("Error uploading file:", {
            err: sanitizeError(uploadError),
          });
          setError(t("errors.loading.logoUploadFailed"));
        }
      } else {
        logger.info("Skipping file upload because:", {
          data: {
            hasPresignedUrl: !!data.presignedUrl,
            hasLogoFile: !!logoFile,
            isNewLogoUploaded: isNewLogoUploaded,
            isFileInstance: logoFile instanceof File,
          },
        });
      }
      setCreatedCompanyData(data);
    } catch (saveError) {
      logger.error(
        `Error ${currentIsEditMode ? "updating" : "submitting"} buyer company:`,
        { err: sanitizeError(saveError) },
      );
    } finally {
      setSaving(false);
      setHasChanges(false);
    }
  };

  const handleExcelFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        ".xlsx",
        ".xls",
      ];

      const fileExtension = file.name.split(".").pop().toLowerCase();

      if (
        !allowedTypes.includes(file.type) &&
        !["xlsx", "xls"].includes(fileExtension)
      ) {
        setExcelError(t("pages.buyerCompanyForm.excelFileTypeError"));
        // Clear the input value to allow re-selecting the same file
        e.target.value = "";
        return;
      }

      setExcelError("");
      setInvalidRows([]);

      // Automatically process the file after selection
      await handleProcessExcelFile(file);

      // Clear the input value to allow re-selecting the same file
      e.target.value = "";
    }
  };

  const handleProcessExcelFile = async (file) => {
    logger.info("processExcelFile", { data: file });

    setSaving(true);
    setExcelError("");
    setInvalidRows([]);
    setImportErrors([]);

    try {
      const validationFunctions = {
        parseExcelFile,
        states,
        towns,
        companySizeOptions,
        countryCodeOptions,
        existingCompanies,
        navigate,
      };

      const result = await processExcelFile(
        navigate,
        file,
        validationFunctions,
        t,
      );

      if (!result.success) {
        setExcelError(result.error);
        setInvalidRows(result.invalidRows || []);
        setImportErrors(result.errors || []);
        setShowExcelErrorModal(true);
        setSaving(false);
        return;
      }

      setImportErrors([]);

      if (result.results && result.results.length > 0) {
        // Show success message and redirect to buyerCompanies
        setShowSuccessMessage(true);
        // Auto-hide success message after 3 seconds and redirect
        setTimeout(() => {
          setIsSlidingUp(true);
          setTimeout(() => {
            setShowSuccessMessage(false);
            setIsSlidingUp(false);
            navigate(BUYER_COMPANIES_PATH, {
              state: { showExcelSuccess: true },
            });
          }, SLIDE_UP_DURATION_MS);
        }, EXCEL_SUCCESS_REDIRECT_MS);
      }
    } catch (excelProcessError) {
      logger.error("Error processing Excel file:", {
        err: sanitizeError(excelProcessError),
      });
      setExcelError(t("pages.buyerCompanyForm.excelProcessingError"));
      setShowExcelErrorModal(true);
    } finally {
      setSaving(false);
    }
  };

  const rfcFormatInvalid = RFC.trim() !== "" && !validateRFC(RFC).isValid;
  const emailFormatInvalid =
    correoElectronicoContactoPrincipal.trim() !== "" &&
    !validateEmail(correoElectronicoContactoPrincipal).isValid;

  const isSaveDisabled = () => {
    return (
      !hasChanges ||
      !razonSocial ||
      !nombreComercial ||
      !validateRFC(RFC).isValid ||
      emailFormatInvalid ||
      !logoFile ||
      (logoFile && !logoFile.name && !logoFile.url)
    );
  };

  const handleBack = () => {
    navigate(BUYER_COMPANIES_PATH);
  };

  if (!allowed) {
    return null;
  }

  return (
    <div className={styles.container}>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        }}
        open={loading && currentIsEditMode}
      >
        <CircularProgress color="inherit" size={60} />
      </Backdrop>
      <div className={styles.header}>
        <div className={styles.languages}>
          <NotificationBell navigate={navigate} />
          <LanguageSelector />
        </div>

        <div className={styles.headerInfoContainer}>
          <div className={styles.headerInfo}>
            <button
              className={styles.returnButtonContainer}
              onClick={handleBack}
            >
              <ChevronLeftIcon className={styles.returnButtonIcon} />
              <span className={styles.returnButtonText}>
                {t("common.goBack")}
              </span>
            </button>
          </div>
          <div className={styles.headerInfo}>
            <SvgIcon sx={{ fontSize: "32px" }} viewBox="0 0 32 32">
              <defs>
                <linearGradient
                  id="gradient1"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#4eabe7" />
                  <stop offset="100%" stopColor="#2f59df" />
                </linearGradient>
              </defs>
              <TitleIcon sx={{ fill: "url(#gradient1)" }} />
            </SvgIcon>
            <h1 className={styles.title}>{t("pages.buyerCompanies.header")}</h1>
          </div>
        </div>
      </div>

      <div className={styles.formContainer}>
        {!currentIsEditMode && (
          <div className={styles.excelSectionContainer}>
            <div className={styles.excelSectionContent}>
              <div className={styles.excelUploadSection}>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleExcelFileChange}
                  className={styles.excelFileInputHidden}
                  id="excelFileInput"
                />
                <div
                  className={`${styles.loadDataButton} ${
                    saving ? styles.disabled : ""
                  }`}
                  onClick={
                    !saving
                      ? () => document.getElementById("excelFileInput").click()
                      : undefined
                  }
                >
                  {saving ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    <>
                      <UploadFileIcon className={styles.excelButtonIcon} />
                      {t("pages.buyerCompanyForm.loadCompanyData")}
                    </>
                  )}
                </div>
              </div>
              <div
                className={styles.downloadExcelButton}
                onClick={() =>
                  handleDownloadBuyerCompaniesExcelLayout(
                    t,
                    companySizeOptions,
                    states,
                    towns,
                    countryCodeOptions,
                  )
                }
              >
                <DownloadFileIcon className={styles.excelButtonIcon} />
                {t("pages.buyerCompanyForm.downloadExcelLayout")}
              </div>
            </div>
          </div>
        )}
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>
            {t("pages.buyerCompanyForm.header")}
          </span>
          <div className={styles.sectionDivider}></div>
        </div>
        <form onSubmit={handleSubmit} className={styles.buyerCompanyForm}>
          <div className={styles.formGroups}>
            <div className={styles.formGroupOne}>
              <div className={styles.imageContainer}>
                <label className={styles.label}>
                  {t("pages.buyerCompanyForm.logo")}
                </label>
                <div className={styles.fileUploadContainer}>
                  <FileUploadArea
                    file={logoFile}
                    isDragging={isDragging}
                    error={error}
                    onFileChange={handleFileChange}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={handleFileClick}
                  />
                  <input
                    id="logoFileInput"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className={styles.fileInput}
                    style={{ display: "none" }}
                  />
                </div>
                <p className={styles.notVisibleFieldMessage}>
                  *{t("pages.buyerCompanyForm.requiredMessage")}
                </p>
              </div>
              <div className={styles.detailsContainer}>
                <div className={styles.formRow}>
                  <div className={styles.formCol}>
                    <label className={styles.label}>
                      {t("pages.buyerCompanyForm.socialReason")}
                    </label>
                    <div className={styles.inputWrapper}>
                      <BuildingIcon className={styles.inputIcon} />
                      <input
                        type="text"
                        name="socialReason"
                        value={razonSocial}
                        onChange={(e) => {
                          handleInputChange(setRazonSocial, e.target.value);
                        }}
                        className={`${styles.input} ${styles.inputWithIcon}`}
                        style={{
                          borderColor:
                            showError && !razonSocial ? "red" : "#909092",
                        }}
                        placeholder={t(
                          "pages.buyerCompanyForm.socialReasonPlaceholder",
                        )}
                      />
                    </div>
                  </div>
                </div>
                <div className={styles.formRow}>
                  <div className={styles.formCol}>
                    <label className={styles.label}>
                      {t("pages.buyerCompanyForm.commercialName")}
                    </label>
                    <div className={styles.inputWrapper}>
                      <BuildingIcon className={styles.inputIcon} />
                      <input
                        type="text"
                        name="commercialName"
                        value={nombreComercial}
                        onChange={(e) => {
                          handleInputChange(setNombreComercial, e.target.value);
                        }}
                        className={`${styles.input} ${styles.inputWithIcon}`}
                        style={{
                          borderColor:
                            showError && !nombreComercial ? "red" : "#909092",
                        }}
                        placeholder={t(
                          "pages.buyerCompanyForm.commercialNamePlaceholder",
                        )}
                      />
                    </div>
                  </div>
                  <div className={styles.formCol}>
                    <label className={styles.label}>
                      {t("pages.buyerCompanyForm.RFC")}
                    </label>
                    <div className={styles.inputWrapper}>
                      <input
                        type="text"
                        name="RFC"
                        value={RFC}
                        onChange={(e) => {
                          handleInputChange(setRFC, e.target.value);
                        }}
                        className={styles.input}
                        style={{
                          borderColor:
                            (showError && !RFC) || rfcFormatInvalid
                              ? "red"
                              : "#909092",
                        }}
                        placeholder={t("pages.buyerCompanyForm.RFCPlaceholder")}
                      />
                    </div>
                    {rfcFormatInvalid && (
                      <p className={styles.formatError}>
                        {t("errors.validation.INVALID_RFC")}
                      </p>
                    )}
                  </div>
                  <div className={styles.formCol}>
                    <label className={styles.label}>
                      {t("pages.buyerCompanyForm.size")}
                    </label>
                    <Select
                      value={getSelectedOption(
                        companySizeOptions,
                        tamanoDeLaEmpresa,
                      )}
                      options={companySizeOptions}
                      placeholder={t("pages.buyerCompanyForm.sizePlaceholder")}
                      styles={selectStyles}
                      className="select__control"
                      onChange={handleCompanySizeChange}
                    />
                  </div>
                </div>
                <div className={styles.formRow}>
                  <div className={styles.formCol}>
                    <label className={styles.label}>
                      {t("pages.buyerCompanyForm.address")}
                    </label>
                    <div className={styles.inputWrapper}>
                      <LocationIcon className={styles.inputIcon} />
                      <input
                        type="text"
                        name="address"
                        value={direccion}
                        onChange={(e) => {
                          handleInputChange(setDireccion, e.target.value);
                        }}
                        className={`${styles.input} ${styles.inputWithIcon}`}
                        style={{
                          borderColor:
                            showError && !direccion ? "red" : "#909092",
                        }}
                        placeholder={t(
                          "pages.buyerCompanyForm.addressPlaceholder",
                        )}
                      />
                    </div>
                  </div>
                  <div className={styles.formCol}>
                    <label className={styles.label}>
                      {t("pages.buyerCompanyForm.state")}
                    </label>
                    <Select
                      ref={addressStateRef}
                      options={states}
                      value={getSafeSelectedOption(states, estado)}
                      placeholder={t("pages.buyerCompanyForm.statePlaceholder")}
                      styles={selectStyles}
                      className="select__control"
                      isSearchable={true}
                      filterOption={filterReferenceOption}
                      noOptionsMessage={noReferenceOptionsMessage}
                      loadingMessage={referenceLoadingMessage}
                      onChange={handleStateChange}
                      getOptionValue={getReferenceOptionValue}
                      formatOptionLabel={formatReferenceOptionLabel}
                    />
                  </div>
                  <div className={styles.formCol}>
                    <label className={styles.label}>
                      {t("pages.buyerCompanyForm.town")}
                    </label>
                    <Select
                      ref={addressTownRef}
                      value={getSafeSelectedOption(filteredTowns, municipio)}
                      options={filteredTowns}
                      placeholder={t("pages.buyerCompanyForm.townPlaceholder")}
                      styles={selectStyles}
                      className="select__control"
                      isSearchable={true}
                      filterOption={filterReferenceOption}
                      noOptionsMessage={noReferenceOptionsMessage}
                      loadingMessage={referenceLoadingMessage}
                      onChange={handleTownChange}
                      getOptionValue={getReferenceOptionValue}
                      formatOptionLabel={formatReferenceOptionLabel}
                    />
                  </div>
                </div>
                <div className={styles.formRow}>
                  <div className={styles.formCol}>
                    <label className={styles.label}>
                      {t("pages.buyerCompanyForm.principalContact")}
                    </label>
                    <div className={styles.inputWrapper}>
                      <PersonIcon className={styles.inputIcon} />
                      <input
                        type="text"
                        name="principalContact"
                        value={nombreContactoPrincipal}
                        onChange={(e) => {
                          handleInputChange(
                            setNombreContactoPrincipal,
                            e.target.value,
                          );
                        }}
                        className={`${styles.input} ${styles.inputWithIcon}`}
                        style={{
                          borderColor:
                            showError && !nombreContactoPrincipal
                              ? "red"
                              : "#909092",
                        }}
                        placeholder={t(
                          "pages.buyerCompanyForm.principalContactPlaceholder",
                        )}
                      />
                    </div>
                  </div>
                  <div className={styles.formCol}>
                    <label className={styles.label}>
                      {t("pages.buyerCompanyForm.email")}
                    </label>
                    <div className={styles.inputWrapper}>
                      <EmailIcon className={styles.inputIcon} />
                      <input
                        type="email"
                        name="email"
                        value={correoElectronicoContactoPrincipal}
                        onChange={(e) => {
                          handleInputChange(
                            setCorreoElectronicoContactoPrincipal,
                            e.target.value,
                          );
                        }}
                        className={`${styles.input} ${styles.inputWithIcon}`}
                        style={{
                          borderColor:
                            (showError && !correoElectronicoContactoPrincipal) ||
                            emailFormatInvalid
                              ? "red"
                              : "#909092",
                        }}
                        placeholder={t(
                          "pages.buyerCompanyForm.emailPlaceholder",
                        )}
                      />
                    </div>
                    {emailFormatInvalid && (
                      <p className={styles.formatError}>
                        {t("errors.validation.INVALID_EMAIL")}
                      </p>
                    )}
                  </div>
                  <div className={styles.formCol}>
                    <label className={styles.label}>
                      {t("pages.buyerCompanyForm.position")}
                    </label>
                    <input
                      type="text"
                      name="position"
                      value={cargoContactoPrincipal}
                      onChange={(e) => {
                        handleInputChange(
                          setCargoContactoPrincipal,
                          e.target.value,
                        );
                      }}
                      className={styles.input}
                      style={{
                        borderColor:
                          showError && !cargoContactoPrincipal
                            ? "red"
                            : "#909092",
                      }}
                      placeholder={t(
                        "pages.buyerCompanyForm.positionPlaceholder",
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.saveSectionContainer}>
            <div className={styles.saveSectionContent}>
              <div
                className={styles.cancelButton}
                onClick={handleBack}
              >
                {t("pages.buyerCompanyForm.cancelButton")}
              </div>
              <div
                className={`${styles.saveButton} ${
                  isSaveDisabled() ? styles.disabled : ""
                }`}
                onClick={!isSaveDisabled() ? handleSubmit : undefined}
              >
                {saving ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  <>
                    <SvgIcon
                      component={SaveIcon}
                      fontSize="small"
                      className={styles.saveButtonIcon}
                    />
                    {t(
                      currentIsEditMode
                        ? "pages.buyerCompanyForm.updateBuyerCompany"
                        : "pages.buyerCompanyForm.createBuyerCompany",
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </form>

        <CustomerCompanyUsers
          companyId={currentIsEditMode ? currentCompanyData._id : null}
          isNewCompany={!currentIsEditMode}
          navigate={navigate}
        />

        {/* Excel Error Modal */}
        <ExcelErrorModal
          open={showExcelErrorModal}
          onClose={() => setShowExcelErrorModal(false)}
          errorMessage={excelError}
          invalidRows={invalidRows}
          importErrors={importErrors}
        />

        {/* Success Message */}
        {showSuccessMessage && (
          <div
            className={`${styles.successMessage} ${
              isSlidingUp ? styles.slideUp : ""
            }`}
          >
            <CheckCircleIcon className={styles.successMessageIcon} />
            {t("pages.buyerCompanyForm.excelFileSuccessMessage")}
          </div>
        )}
      </div>
    </div>
  );
};

export default BuyerCompanyForm;
